import {
  useCreateNewsItem,
  useDeleteNews,
  useUpdateNewsItem,
} from "@/api/cms.ts";
import { uploadImage } from "@/api/image";
import { Button } from "@/components/ui/button.tsx";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import { getMainLanguage, SupportedLocale } from "@/i18n";
import { NewsItem, newsItemSchema } from "@/lib/cms/cms-types";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CroppedImageInput } from "../form-fields/CroppedImageInput";
import { Col, RichText, Text } from "../form-items/AbstractForm";
import { MultilingualField } from "../form-items/MultilingualField";
import { generateImageName } from "@/lib/utils";
import { Input } from "../ui/input";
import { Controller } from "react-hook-form";

const newsSchemas = newsItemSchema.pick({
  title: true,
  content: true,
  date: true,
});

type FormData = z.infer<typeof newsSchemas>;

const NewsForm = ({
  newsItem,
  refetch,
}: {
  newsItem?: NewsItem;
  refetch: () => void;
}) => {
  const { t } = useTranslation();
  const { instanceData } = useAuth();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageData, setImageData] = useState<any>(null);
  const [currentImage, setCurrentImage] = useState<string | null>(null);

  const defaultValues: FormData = useMemo(
    () => ({
      title: newsItem?.title,
      content: newsItem?.content,
      date: newsItem?.date ? new Date(newsItem.date) : new Date(),
    }),
    [newsItem],
  );

  const form = useForm<FormData>({
    resolver: zodResolver(newsSchemas),
    defaultValues,
  });

  const { handleSubmit } = form;

  const create = useCreateNewsItem();
  const update = useUpdateNewsItem();

  const deleteNews = useDeleteNews();

  const handleDelete = (newsId: string) => {
    deleteNews.mutate(newsId, {
      onSuccess: () => {
        toast({
          title: t("success"),
          description: t("newsDeleteSuccessMessage"),
          duration: 1000,
          variant: "success",
        });
        form.reset();
      },
      onError: () => {
        toast({
          title: t("error"),
          description: t("newsDeleteErrorMessage"),
          duration: 1000,
          variant: "error",
        });
      },
    });
  };

  const onSubmit = (data: FormData) => {
    const mutationMethod = newsItem?.id ? update : create;

    if (instanceData == null) return;

    mutationMethod.mutate(
      {
        id: newsItem?.id,
        ...data,
      },
      {
        onSuccess: async (data) => {
          if (imageFile && imageData) {
            // Send image to converter here
            await uploadImage(
              imageFile,
              {
                ...imageData,
                details: "",
                name: generateImageName({
                  primaryType: "thumbnail",
                  instanceName: instanceData.name,
                  pageNameInDefaultLanguage: "news",
                  title: data.title,
                }),
                docId: data.id,
                position: "newsImage",
              },
              instanceData.s3Bucket,
              instanceData.id,
            );
          }
          toast({
            title: t("success"),
            description: t("newsSuccessMessage"),
            duration: 1000,
            variant: "success",
          });
        },
        onError: () => {
          toast({
            title: t("error"),
            description: t("newsErrorMessage"),
            duration: 1000,
            variant: "error",
          });
        },
      },
    );
  };

  useEffect(() => {
    if (newsItem) {
      form.reset(defaultValues);
      // Vérifier s'il y a une image dans les attachments
      const newsImage =
        newsItem.attachments.length > 0 ? newsItem.attachments[0] : null;
      if (newsImage?.mediumPublicUrl) {
        setCurrentImage(newsImage.mediumPublicUrl);
      }
    }
  }, [newsItem, defaultValues, form]);

  const onImageChange = (file: File, data: any) => {
    setImageFile(file);
    setImageData(data);
    setCurrentImage(URL.createObjectURL(file));
    toast({
      title: t("success"),
      description: t("newsSuccessMessage"),
      duration: 1000,
      variant: "success",
    });
  };

  const formatDateInput = (val: unknown): string => {
    if (!val) return "";
    if (val instanceof Date) return val.toISOString().split("T")[0];
    if (typeof val === "string") return val.split("T")[0];
    return "";
  };

  return (
    <div className="gap-6 flex flex-col overflow-y-auto">
      {(currentImage || imageFile) && (
        <div className="flex flex-col items-start gap-2">
          <img
            src={
              currentImage ||
              (imageFile ? URL.createObjectURL(imageFile) : undefined)
            }
            alt="Current news image"
            className="max-w-full h-auto rounded-md shadow-sm"
          />
          <div className="text-sm text-green-600">
            {imageFile
              ? `${t("newImageSelected")}: ${t(imageFile.name)}`
              : t("currentImage")}
          </div>
        </div>
      )}
      <CroppedImageInput
        buttonLabel={currentImage ? t("modifyImage") : t("addImage")}
        docId={newsItem?.id}
        attachment={
          newsItem && newsItem.attachments.length > 0
            ? newsItem.attachments[0]
            : undefined
        }
        refetch={refetch}
        imagePosition={"newsImage"}
        aspect={16 / 9}
        onUpdate={onImageChange}
        buildImageName={(instanceName) =>
          generateImageName({
            primaryType: "thumbnail",
            instanceName,
            pageNameInDefaultLanguage: "news",
            title: newsItem?.title?.[getMainLanguage()],
          })
        }
      />

      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="gap-6 flex flex-col">
          <MultilingualField
            defaultLang={getMainLanguage() as SupportedLocale}
            render={(lang) => (
              <Col>
                <Text
                  name={`title.${lang}`}
                  label={t("cms.newsItem.form.title")}
                />
                <RichText
                  name={`content.${lang}`}
                  label={t("cms.newsItem.form.content")}
                />
              </Col>
            )}
          />
          <Controller
            control={form.control}
            name="date"
            render={({ field }) => (
              <Input
                label={t("cms.newsItem.form.date")}
                type="date"
                value={formatDateInput(field.value)}
                onChange={(e) => {
                  const value = e.target.value;
                  field.onChange(value ? new Date(value) : undefined);
                }}
              />
            )}
          />

          {newsItem?.id != undefined && (
            <Button
              className="flex gap-1 text-sm items-center text-red-600 justify-center cursor-pointer"
              variant="not_selected"
              type="reset"
              onClick={() => handleDelete(newsItem.id as string)}
            >
              <Trash2 className={"w-4 h-4"} />
              <p>{t("delete")}</p>
            </Button>
          )}
          <hr />
          <Button type="submit">{t("addNewsSubmitButton")}</Button>
        </form>
      </FormProvider>
    </div>
  );
};

export default NewsForm;
